import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from '@shared/routing';

import {
	CheckinPassengerFragment,
	CheckinSegmentFragment,
	FlightSeatMapSegmentFragment,
	SeatType,
	SelectedSeatFragment,
	TravellerFieldEnum,
	TravellerGdsService,
	useCheckinSaveOrderServicesMutation,
	useGetCheckinOrderQuery
} from '@websky/graphql';
import { TravellerSelectedSeat, TravellerSelectedServices } from '@websky/graphql';
import {
	getCheckinOrderId,
	getCheckinHasStarted,
	hasMealServices,
	hasBaggageServices,
	getCheckinNotStarted,
	getCheckinIsBlockedForPayment,
	hasUpgradeServices,
	getIsCheckinCompletedOrPreseating,
	hasExtrasStepServices,
	hasInsuranceSeparateStep
} from './store/order/selectors';
import { fillCheckinOrder } from './store/order/actions';
import { CheckinStep, ServiceModificationHookResults } from './types';
import { RunServiceRequestPassengers } from '../../Checkout/types';
import { SelectedServicesState } from './store/selectedServices/reducers';
import { getUserValue } from '../../utils';
import { useIataCode } from '../../hooks';
import { GdsService_OrderAdditionalServiceGdsServiceBaggage_Fragment } from '@websky/graphql';
import { groupSeatsBySegment } from './components/Checkin/Order/utils';
import { hasRestrictions as hasRestrictionsSelector } from '@entities/restrictions';

const PASSENGERS_PATHNAME_PATTERN = /^\/(\d+)\/?$/;

export const getTravellerRetryLink = (traveller: CheckinPassengerFragment): string => {
	const lastName = getUserValue(traveller, TravellerFieldEnum.LastName);

	const ticket = traveller.tickets?.[0]?.number;

	return `/search/${ticket}/${lastName}`;
};

/**
 * Проверяет наличие сегмента шага в pathname (например, `/seats` или `/seats/confirm`).
 */
const hasStepSegment = (pathname: string, step: CheckinStep): boolean => {
	return new RegExp(`/${step}(?:/|$)`).test(pathname);
};

const defaultSteps = [
	CheckinStep.Passengers,
	CheckinStep.Upgrade,
	CheckinStep.Baggage,
	CheckinStep.Meal,
	CheckinStep.Seats,
	CheckinStep.Insurance,
	CheckinStep.Extras,
	CheckinStep.Restrictions,
	CheckinStep.Payment
];

const y7Steps = [
	CheckinStep.Passengers,
	CheckinStep.Upgrade,
	CheckinStep.Insurance,
	CheckinStep.Extras,
	CheckinStep.Baggage,
	CheckinStep.Meal,
	CheckinStep.Seats,
	CheckinStep.Payment
];

export const useSteps = () => {
	const orderId = useSelector(getCheckinOrderId);
	const checkinHasStarted = useSelector(getCheckinHasStarted);
	const checkinIsBlockedForPayment = useSelector(getCheckinIsBlockedForPayment);
	const hasMeals = useSelector(hasMealServices);
	const hasBaggage = useSelector(hasBaggageServices);
	const hasRestrictions = useSelector(hasRestrictionsSelector);
	const hasExtrasServices = useSelector(hasExtrasStepServices);
	const hasUpgrade = useSelector(hasUpgradeServices);
	const insuranceAsSeparateStep = useSelector(hasInsuranceSeparateStep);
	const isCheckinCompletedOrPreseating = useSelector(getIsCheckinCompletedOrPreseating);
	const history = useHistory();
	const iataCode = useIataCode();

	const steps = iataCode === 'Y7' ? y7Steps : defaultSteps;

	const availableSteps = steps.filter(step => {
		switch (step) {
			case CheckinStep.Upgrade:
				return hasUpgrade;

			case CheckinStep.Baggage:
				return hasBaggage;

			case CheckinStep.Restrictions:
				return hasRestrictions;

			case CheckinStep.Meal:
				return hasMeals;

			case CheckinStep.Extras:
				return hasExtrasServices;

			case CheckinStep.Insurance:
				return insuranceAsSeparateStep;

			default:
				return true;
		}
	});

	const result = {
		setStep: (step: CheckinStep, searchParams?: string) => {
			if (searchParams) {
				history.push({ pathname: `/${orderId}/${step}`, search: searchParams });
			} else {
				history.push(`/${orderId}/${step}`);
			}
		},
		/**
		 * ignoreBlockedForPayment нужен для навигации сразу после снятия блокировки заказа
		 * (см. useCheckinPaymentFailedHandlers), когда checkinIsBlockedForPayment в замыкании ещё не успел
		 * обновиться до актуального значения из стора
		 */
		getNextStepEnum: (currentStep?: CheckinStep, ignoreBlockedForPayment = false): CheckinStep => {
			if (currentStep === CheckinStep.Passengers && isCheckinCompletedOrPreseating) {
				return CheckinStep.Passengers;
			} else if (checkinIsBlockedForPayment && !ignoreBlockedForPayment) {
				return CheckinStep.Payment;
			} else {
				const stepsList = checkinHasStarted ? availableSteps : steps;
				const currentStepIndex = Math.max(stepsList.indexOf(currentStep), 0);

				if (currentStepIndex === stepsList.length - 1) {
					return currentStep;
				} else {
					return stepsList[currentStepIndex + 1];
				}
			}
		},
		getNextStep: (currentStep?: CheckinStep, ignoreBlockedForPayment = false): string => {
			return `/${orderId}/${result.getNextStepEnum(currentStep, ignoreBlockedForPayment)}`;
		},
		goToNextStep: (currentStep: CheckinStep, replace = false, ignoreBlockedForPayment = false) => {
			const newURL = result.getNextStep(currentStep, ignoreBlockedForPayment);

			if (replace) {
				history.replace(newURL);
			} else {
				history.push(newURL);
			}
		},
		getPreviousStepEnum: (currentStep: CheckinStep): CheckinStep => {
			if (checkinHasStarted) {
				const currentStepIndex = availableSteps.indexOf(currentStep);

				if (currentStepIndex === 0) {
					return currentStep;
				} else {
					return availableSteps[availableSteps.indexOf(currentStep) - 1];
				}
			}

			return currentStep;
		},
		previousStep: (currentStep: CheckinStep, replace = false) => {
			const newURL = `/${orderId}/${result.getPreviousStepEnum(currentStep)}`;

			if (replace) {
				history.replace(newURL);
			} else {
				history.push(newURL);
			}
		},
		getStepFromPathname: (pathname: string): CheckinStep | undefined => {
			if (PASSENGERS_PATHNAME_PATTERN.test(pathname)) {
				return CheckinStep.Passengers;
			}

			for (const step of availableSteps) {
				if (step !== CheckinStep.Passengers && hasStepSegment(pathname, step)) {
					return step;
				}
			}

			return undefined;
		},
		availableSteps
	};

	return result;
};

export const useServiceRequest = (): ServiceModificationHookResults => {
	const dispatch = useDispatch();
	const orderId = useSelector(getCheckinOrderId);

	const [save] = useCheckinSaveOrderServicesMutation();

	const request = async (services: TravellerSelectedServices[]): Promise<any> => {
		const { data, errors } = await save({
			variables: {
				params: {
					id: orderId,
					services
				}
			}
		});

		if (errors) {
			throw errors;
		} else if (data && data.CheckinSaveOrderServices) {
			dispatch(fillCheckinOrder(data.CheckinSaveOrderServices));

			return data.CheckinSaveOrderServices;
		}
	};

	/**
	 * This is a hack to support old RunServiceRequest thing.
	 * Do not use this intentionally.
	 *
	 * @deprecated
	 */
	const runServiceRequest = async (passengers: RunServiceRequestPassengers[]) =>
		await request(
			passengers.map(
				(passenger): TravellerSelectedServices => ({
					travellerId: passenger.passengerId,
					seats: passenger.seats
						? passenger.seats.map(
								(seat): TravellerSelectedSeat => ({
									segmentId: seat.segment.id,
									letter: seat.letter,
									row: seat.row
								})
						  )
						: [],
					gdsServices: {
						setServices: passenger.setServices
					}
				})
			)
		);

	return {
		runServiceRequest
	};
};

export const getRelatedSegments = (groups: string[][], segmentId: string = null): Set<string> => {
	const result = new Set<string>();

	if (segmentId === null) {
		groups.forEach(group => {
			result.add(group[0]);
		});
	} else {
		groups.some(group => {
			if (group.includes(segmentId)) {
				group.forEach(segmentId => result.add(segmentId));

				return true;
			}

			return false;
		});
	}

	return result;
};

export const createServicesForRequest = (
	selectedServices: SelectedServicesState,
	baggageServices?: GdsService_OrderAdditionalServiceGdsServiceBaggage_Fragment[]
): TravellerSelectedServices[] => {
	const passengers = new Map<string, TravellerSelectedServices>();

	for (const passengerId in selectedServices) {
		if (selectedServices.hasOwnProperty(passengerId)) {
			const passengerServices = selectedServices[passengerId];
			let ignoreServiceId: string = null;

			for (const serviceId in passengerServices) {
				if (passengerServices.hasOwnProperty(serviceId)) {
					const servicesBySegments = passengerServices[serviceId];
					const relatedService = baggageServices?.find(baggageService => baggageService.id === serviceId);

					for (const segmentId in servicesBySegments) {
						if (servicesBySegments.hasOwnProperty(segmentId)) {
							const count = servicesBySegments[segmentId];

							if (ignoreServiceId === serviceId) {
								ignoreServiceId = null;
								continue;
							}

							if (!passengers.has(passengerId)) {
								passengers.set(passengerId, {
									travellerId: passengerId,
									gdsServices: {
										setServices: []
									}
								});
							}

							const passenger = passengers.get(passengerId);

							// Sometimes service should be added to a multiple segments at the same time.
							// This happens when service is tightly bound to a whole trip itself (to a "leg" in terms of Nemo),
							// rather than to a particular segment. in this case, just ignore next segment in selectedServices
							if (
								relatedService?.allowedSegments.some(allowedSegment =>
									allowedSegment.includes(segmentId)
								)
							) {
								ignoreServiceId = serviceId;

								passenger.gdsServices.setServices.push({
									serviceId,
									count,
									segmentIds: [
										...relatedService?.allowedSegments.find(allowedSegment =>
											allowedSegment.includes(segmentId)
										)
									]
								});
							} else {
								passenger.gdsServices.setServices.push({
									serviceId,
									count,
									segmentIds: [segmentId]
								});
							}
						}
					}
				}
			}
		}
	}

	return [...passengers.values()];
};

export const useCheckinAvailabilityInfo = (pollInterval: number = 60000) => {
	const [showExpireModal, setShowExpireModal] = useState(false);
	const isCheckinNew = useSelector(getCheckinNotStarted);
	const isCheckinStarted = useSelector(getCheckinHasStarted);
	const checkinOrderId = useSelector(getCheckinOrderId);

	const { startPolling, stopPolling, loading, data } = useGetCheckinOrderQuery({
		variables: {
			id: checkinOrderId
		},
		skip: !checkinOrderId
	});

	useEffect(() => {
		if (checkinOrderId) {
			startPolling(pollInterval);
		}

		return stopPolling;
	}, [checkinOrderId]);

	const isAvailable = data?.CheckinOrder?.segments?.some(segment => segment.checkinInfo?.checkinAvailable);

	const open = useCallback(() => setShowExpireModal(true), []);
	const close = useCallback(() => setShowExpireModal(false), []);

	useEffect(() => {
		if ((isCheckinStarted || isCheckinNew) && checkinOrderId && !loading && !isAvailable) {
			setShowExpireModal(true);
			stopPolling();
		}
	}, [isAvailable]);

	return { loading, isAvailable, showExpireModal, open, close, stopPolling };
};

export const getThroughSegmentsIds = (segment: CheckinSegmentFragment) =>
	segment.checkinWithSegments.map(throughSegment => throughSegment.id);

export const isSegmentCancellationAvailable = (segment: CheckinSegmentFragment, segments: CheckinSegmentFragment[]) => {
	return [segment.id, ...getThroughSegmentsIds(segment)].every(
		segmentId => segments.find(({ id }) => id === segmentId)?.checkinInfo.cancelAvailable
	);
};

export const isAllSegmentsThrough = (segments: CheckinSegmentFragment[]) =>
	segments.every(segment => segment.checkinWithSegments.length === segments.length);

export const checkIfAllSeatsConfirmed = (travellers: CheckinPassengerFragment[], segmentsLength: number): boolean => {
	return travellers
		.filter(p => p.type !== 'INF')
		.every(p => {
			const seats = p.services?.seats;

			return seats?.every(s => s.isConfirmed) && seats.length >= segmentsLength;
		});
};

export const prepareServicesPayload = (
	travellers: CheckinPassengerFragment[],
	servicesMap: Map<string, TravellerGdsService>
): TravellerSelectedServices[] => {
	return travellers.map(passenger => {
		const seats = passenger?.services?.seats || [];
		const seatsBySegments = groupSeatsBySegment(seats);

		return {
			travellerId: passenger.id,
			gdsServices: servicesMap.get(passenger.id) ?? {},
			seats: flattenSeatsWithType(seatsBySegments)
		};
	});
};

export const flattenSeatsWithType = (seatsBySegments: SelectedSeatFragment[][]): TravellerSelectedSeat[] => {
	const result: TravellerSelectedSeat[] = [];

	for (const segmentSeats of seatsBySegments) {
		const lastIndex = segmentSeats.length - 1;

		for (let i = 0; i <= lastIndex; i++) {
			const seat = segmentSeats[i];
			result.push({
				segmentId: seat.segment?.id ?? null,
				letter: seat.letter,
				row: seat.row,
				type: segmentSeats.length > 1 ? (i === lastIndex ? SeatType.extraSeat : SeatType.common) : null
			});
		}
	}

	return result;
};

export const validateOrderSeats = (
	travellers: CheckinPassengerFragment[],
	segments: FlightSeatMapSegmentFragment[]
): boolean => {
	const requiredSegmentsCount = segments.filter(s => s.availableSeatsCount > 0).length;

	return travellers.every(t => t.type === 'INF' || (t.services?.seats?.length ?? 0) >= requiredSegmentsCount);
};
